import React, {useState, useEffect, useMemo, useRef} from 'react';
import {
    Package, Edit3, AlertTriangle, ArrowUp,
    ArrowDown, FileText, X, History, Loader, ArrowRight,
    CheckCircle, Plus, Minus, Truck, User,
    Building2, Mail, RefreshCw, ClipboardList, ChevronDown, ChevronUp,
    ShoppingCart, Download, Upload, FileSpreadsheet, Layers, ChevronRight, Trash2, Save
} from 'lucide-react';
import {
    HeaderAction, SearchInput, ToolbarSelect, ToolbarDate, FilterButton, ActiveFilters, SummaryStrip,
    ListLoader, ListEmpty, ListCard, thClass, mobileListClass, mobileRowClass, Avatar, RowActions, Pagination,
    Modal, ModalFooter
} from './StockUI';
import {inputClass as formInputClass} from '../ClientsTable/clientFormUI';
import moment from 'moment';
import 'moment/locale/fr';
import secureStorage from "../utils/secureStorage";
import {api} from "../config/api";
import StockReception from './StockReception';
import InventairePage from '../components/Inventaire/InventairePage';
import usePersistedTab from "../hooks/usePersistedTab";
import ReferencePickerModal from '../components/Common/ReferencePickerModal';
import {categoriesConfig, brandsConfig, productsConfig} from '../components/Common/referenceConfigs';
import AppleSelect from '../components/Common/AppleSelect';
import Button from '../components/Common/Button';
import {tabClass, tabIconClass} from '../components/Common/TabBar';
import DateInput from "../components/Common/DateInput";

// ─── Provider Modal (must be outside Stock to avoid remount on every render) ─

// Catalogue form field
const providerFieldClass = formInputClass(false);
const fieldLabelClass = 'text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block';

// Success / error line shown inside a modal body
const ModalMessage = ({message}) => message ? (
    <p className={`flex items-center gap-2 text-sm ${message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
        {message.type === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0"/> : <AlertTriangle className="w-4 h-4 flex-shrink-0"/>}
        {message.text}
    </p>
) : null;

// « Réinitialiser » link at the end of a filter form
const ResetLink = ({onClick}) => (
    <div className="flex justify-end">
        <Button variant="ghost" size="sm" icon={RefreshCw} onClick={onClick}>Réinitialiser</Button>
    </div>
);

// Same look as the catalogue import popup: centred title, « Annuler | Action » footer
const ProviderModal = ({ isEdit, form, setForm, onClose, onSave }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg shadow-lg w-full sm:max-w-2xl sm:mx-4 overflow-hidden">
            <div className="relative px-12 py-5 border-b border-gray-200 dark:border-gray-700 text-center">
                <h3 className="text-lg font-medium text-gray-800 dark:text-white">
                    {isEdit ? 'Modifier le fournisseur' : 'Nouveau fournisseur'}
                </h3>
                <button onClick={onClose} aria-label="Fermer"
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <X className="w-4 h-4"/>
                </button>
            </div>
            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="sm:col-span-2">
                        <label htmlFor="provider-name" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Nom <span className="text-red-500">*</span> :
                        </label>
                        <input id="provider-name" type="text" value={form.name}
                               onChange={(e) => setForm(prev => ({...prev, name: e.target.value}))}
                               className={providerFieldClass}/>
                    </div>
                    <div>
                        <label htmlFor="provider-email" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Email :
                        </label>
                        <input id="provider-email" type="email" value={form.email}
                               onChange={(e) => setForm(prev => ({...prev, email: e.target.value}))}
                               className={providerFieldClass}/>
                    </div>
                    <div>
                        <label htmlFor="provider-phone" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Téléphone :
                        </label>
                        <input id="provider-phone" type="tel" value={form.phone}
                               onChange={(e) => setForm(prev => ({...prev, phone: e.target.value}))}
                               className={providerFieldClass}/>
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="provider-address" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Adresse :
                        </label>
                        <input id="provider-address" type="text" value={form.address}
                               onChange={(e) => setForm(prev => ({...prev, address: e.target.value}))}
                               className={providerFieldClass}/>
                    </div>
                    <div>
                        <label htmlFor="provider-city" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Ville :
                        </label>
                        <input id="provider-city" type="text" value={form.city}
                               onChange={(e) => setForm(prev => ({...prev, city: e.target.value}))}
                               className={providerFieldClass}/>
                    </div>
                    <div>
                        <label htmlFor="provider-country" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Pays :
                        </label>
                        <input id="provider-country" type="text" value={form.country}
                               onChange={(e) => setForm(prev => ({...prev, country: e.target.value}))}
                               className={providerFieldClass}/>
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="provider-notes" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                            Notes (optionnelles) :
                        </label>
                        <textarea id="provider-notes" value={form.notes}
                                  onChange={(e) => setForm(prev => ({...prev, notes: e.target.value}))}
                                  rows="2" className={providerFieldClass}/>
                    </div>
                </div>
            </div>
            <div className="grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
                <button onClick={onClose}
                        className="py-4 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium">
                    Annuler
                </button>
                <button onClick={onSave}
                        className="py-4 text-primary font-medium border-l border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                    {isEdit ? 'Enregistrer' : 'Ajouter'}
                </button>
            </div>
        </div>
    </div>
);

// ─── Custom Components ───────────────────────────────────────────────────────

// Same thumbnail as the catalogue list: item colour + two initials
const ItemAvatar = ({item, size}) => <Avatar name={item.name} color={item.color} size={size}/>;

// Catalogue-style category chip (tinted with the category colour)
const CategoryChip = ({name, color}) => name ? (
    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium shadow-sm"
          style={{backgroundColor: `${color || '#4DB6AC'}20`, color: color || '#4DB6AC'}}>
        {name}
    </span>
) : <span className="text-gray-400 text-sm">—</span>;

// Stock progress bar: shows current vs min threshold
const StockBar = ({current, min}) => {
    if (!min || min <= 0) return null;
    const ratio = Math.min(current / (min * 3), 1); // fill relative to 3× minStock
    const color = current <= 0 ? 'bg-red-500' : current < min ? 'bg-amber-400' : 'bg-primary';
    return (
        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 mt-1">
            <div className={`h-1.5 rounded-full transition-all ${color}`} style={{width: `${Math.max(ratio * 100, 2)}%`}}/>
        </div>
    );
};

const StatusBadge = ({status, isLowStock, currentStock}) => {
    let config = {bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400', icon: null, label: 'Inconnu'};

    if (status === 'in-stock' || (!isLowStock && currentStock > 0)) {
        config = {bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', icon: CheckCircle, label: 'En stock'};
    } else if (status === 'low-stock' || isLowStock) {
        config = {bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', icon: AlertTriangle, label: 'Stock bas'};
    } else if (status === 'out-of-stock' || currentStock <= 0) {
        config = {bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: AlertTriangle, label: 'Rupture'};
    }

    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
            {config.icon && <config.icon className="w-3 h-3"/>}
            {config.label}
        </span>
    );
};

// Every distinct `type` a StockMovement can carry (see clicoo StockUpdateService /
// StockAdjustmentController / PurchaseOrderController / RecipeStockEngine / MenuSaleService).
// Shared between the badge, the "ventes uniquement" filter, and the group-by-type view.
const MOVEMENT_TYPE_CONFIG = {
    sale:                {bg: 'bg-red-100 dark:bg-red-900/30',    text: 'text-red-700 dark:text-red-400',    icon: ShoppingCart, label: 'Vente',                isSale: true},
    sale_update:         {bg: 'bg-red-100 dark:bg-red-900/30',    text: 'text-red-700 dark:text-red-400',    icon: ShoppingCart, label: 'Vente (modifiée)',     isSale: true},
    sale_revert:         {bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', icon: ShoppingCart, label: 'Vente annulée',        isSale: true},
    sale_menu:           {bg: 'bg-red-100 dark:bg-red-900/30',    text: 'text-red-700 dark:text-red-400',    icon: ShoppingCart, label: 'Vente (menu/pack)',    isSale: true},
    receipt:             {bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400',  icon: ArrowDown,    label: 'Réception'},
    issue:               {bg: 'bg-red-100 dark:bg-red-900/30',    text: 'text-red-700 dark:text-red-400',    icon: ArrowUp,      label: 'Sortie manuelle'},
    return:              {bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', icon: ArrowDown,    label: 'Retour'},
    adjustment:          {bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-400',   icon: Edit3,        label: 'Ajustement'},
    inventory_adjustment:{bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-400',   icon: Edit3,        label: 'Ajustement inventaire'},
    reception:           {bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400',  icon: Truck,        label: 'Réception fournisseur'},
    reception_cancel:    {bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-400', icon: Truck,        label: 'Réception annulée'},
    recipe_consumption:  {bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', icon: Package,      label: 'Consommation recette'},
    import:              {bg: 'bg-teal-100 dark:bg-teal-900/30',   text: 'text-teal-700 dark:text-teal-300',   icon: Upload,       label: 'Import catalogue'},
    'import-reset':      {bg: 'bg-gray-100 dark:bg-gray-700',   text: 'text-gray-600 dark:text-gray-400',   icon: Upload,       label: 'Import (remise à zéro)'},
};
const getMovementTypeConfig = (type) =>
    MOVEMENT_TYPE_CONFIG[type] || {bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-600 dark:text-gray-400', icon: Edit3, label: type || 'Autre'};

// Sale movements store `reference` as "SALE-<saleId>-<saleItemId>" — pull out a short,
// human-readable pointer to the actual sale so it's obvious which order moved the stock.
const parseSaleReference = (reference) => {
    if (!reference || !reference.startsWith('SALE-')) return null;
    const saleId = reference.slice('SALE-'.length).split('-').slice(0, 5).join('-');
    return saleId ? saleId.slice(0, 8) : null;
};

const MovementTypeBadge = ({type}) => {
    const {bg, text, icon: Icon, label} = getMovementTypeConfig(type);

    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
            <Icon className="w-3 h-3"/>
            {label}
        </span>
    );
};

const POStatusBadge = ({status}) => {
    const configs = {
        draft:     {bg: 'bg-gray-100 dark:bg-gray-700',   text: 'text-gray-600 dark:text-gray-400',   dot: 'bg-gray-400 dark:bg-gray-500',   label: 'Brouillon'},
        confirmed: {bg: 'bg-blue-100 dark:bg-blue-900/30',   text: 'text-blue-700 dark:text-blue-400',   dot: 'bg-blue-500',   label: 'Confirmé'},
        received:  {bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400',  dot: 'bg-green-500',  label: 'Reçu'},
        cancelled: {bg: 'bg-red-100 dark:bg-red-900/30',    text: 'text-red-700 dark:text-red-400',    dot: 'bg-red-500',    label: 'Annulé'},
        pending:   {bg: 'bg-amber-100 dark:bg-amber-900/30',  text: 'text-amber-700 dark:text-amber-400',  dot: 'bg-amber-500',  label: 'En attente'},
    };
    const c = configs[status] || configs.draft;
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${c.bg} ${c.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}/>
            {c.label}
        </span>
    );
};

// ─── Stock Component ─────────────────────────────────────────────────────────

const DEFAULT_PO_FILTERS = {status: '', provider_id: '', date_start: '2024-01-01', date_end: '2027-12-31'};

const Stock = () => {
    const [activeTab, setActiveTab] = usePersistedTab('tab_stock', 'inventory');
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [stockItems, setStockItems] = useState([]);
    const [stockHistory, setStockHistory] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    // Counters over the whole filtered stock, sent by the API (not just the current page)
    const [stockSummary, setStockSummary] = useState(null);
    const [quickAdjustError, setQuickAdjustError] = useState(null);
    // sorting is stable — defined outside render to avoid useEffect dependency churn
    const sorting = useMemo(() => ({field: 'name', direction: 'asc'}), []);

    // Providers state
    const [providers, setProviders] = useState([]);
    const [providerSearch, setProviderSearch] = useState('');
    // Bumped by the header "Nouveau" on the Inventaire tab (same pattern as CatalogPage → MenuPage)
    const [inventoryCreateKey, setInventoryCreateKey] = useState(0);
    // Bumped by the header « Enregistrer » / « Vider » of the Réception tab
    const [receptionSubmitKey, setReceptionSubmitKey] = useState(0);
    const [receptionResetKey, setReceptionResetKey] = useState(0);
    const [showProviderModal, setShowProviderModal] = useState(false);
    const [isProviderModalEdit, setIsProviderModalEdit] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [providerForm, setProviderForm] = useState({
        name: '', email: '', phone: '', address: '', city: '', country: '', notes: ''
    });

    // ─── Purchase Orders (Historique des achats) state ──────────────────────
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [poLoading, setPoLoading] = useState(false);
    const [poPage, setPoPage] = useState(1);
    const [poTotalPages, setPoTotalPages] = useState(1);
    const [poTotalItems, setPoTotalItems] = useState(0);
    const [poItemsPerPage, setPoItemsPerPage] = useState(10);
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [poFilters, setPoFilters] = useState(DEFAULT_PO_FILTERS);

    // Filters state
    const [filters, setFilters] = useState({
        category: 'all', brand: 'all', minStock: '', maxStock: '', status: 'all',
        startDate: '', endDate: '', movementType: 'all', variantId: '', productName: '', stockAlert: ''
    });

    // Adjustment state
    const [showAdjustModal, setShowAdjustModal] = useState(false);
    const [adjustmentData, setAdjustmentData] = useState({
        productId: '', variantId: '', productName: '', currentStock: 0, quantity: 1, reason: '', type: 'adjustment'
    });
    const [isAdjusting, setIsAdjusting] = useState(false);
    const [adjustmentMessage, setAdjustmentMessage] = useState(null);

    // Reference picker state for filters
    const [activeFilterPicker, setActiveFilterPicker] = useState(null); // 'category' | 'brand' | null
    const [selectedFilterCategory, setSelectedFilterCategory] = useState(null);
    const [selectedFilterBrand, setSelectedFilterBrand] = useState(null);

    // Export filters state
    const [exportFilters, setExportFilters] = useState({
        category: '',
        brand: '',
        name: '',
        sku: '',
        stock_status: '',
        critical_only: false,
        stock_min: '',
        stock_max: '',
    });
    const [isExporting, setIsExporting] = useState(false);
    const [exportMessage, setExportMessage] = useState(null);
    const [selectedExportCategory, setSelectedExportCategory] = useState(null);
    const [selectedExportBrand, setSelectedExportBrand] = useState(null);
    const [activeExportPicker, setActiveExportPicker] = useState(null); // 'category' | 'brand' | null

    // Stock valuation report (.pdf) state — GET /stock/report
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportFilters, setReportFilters] = useState({
        category: '', product: '', dateStart: '', dateEnd: '',
    });
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [reportMessage, setReportMessage] = useState(null);
    const [selectedReportCategory, setSelectedReportCategory] = useState(null);
    const [selectedReportProduct, setSelectedReportProduct] = useState(null);
    const [activeReportPicker, setActiveReportPicker] = useState(null); // 'category' | 'product' | null

    // Import state
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const fileInputRef = useRef(null);

    moment.locale('fr');

    const isVariant = (item) => item.type === 'variant';
    const variantParentIds = useMemo(
        () => new Set(stockItems.filter(i => i.type === 'variant').map(i => i.productId)),
        [stockItems]
    );
    const parentHasVariants = (item) => !isVariant(item) && (item.hasVariants || item.variants?.length > 0 || variantParentIds.has(item.id));
    const [collapsedParents, setCollapsedParents] = useState(new Set());
    const toggleParent = (id) => setCollapsedParents(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const visibleItems = useMemo(
        () => stockItems.filter(i => !(i.type === 'variant' && collapsedParents.has(i.productId))),
        [stockItems, collapsedParents]
    );
    const TypeBadge = ({item}) => {
        if (isVariant(item)) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                    <Layers className="w-3 h-3"/>Variante
                </span>
            );
        }
        if (!parentHasVariants(item)) return null;
        const count = item.variantsCount ?? stockItems.filter(i => i.type === 'variant' && i.productId === item.id).length;
        return (
            <button onClick={() => toggleParent(item.id)}
                    title={collapsedParents.has(item.id) ? 'Déplier les variantes' : 'Plier les variantes'}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary rounded-full text-xs font-medium hover:bg-primary/20 dark:hover:bg-primary/30 transition">
                <ChevronRight className={`w-3 h-3 transition-transform ${collapsedParents.has(item.id) ? '' : 'rotate-90'}`}/>
                {count} variante{count > 1 ? 's' : ''}
            </button>
        );
    };

    // ─── Data Fetching ───────────────────────────────────────────────────────
    // silent: refresh the rows in place without the full-table spinner
    const fetchStockItems = async ({silent = false} = {}) => {
        if (!silent) setIsLoading(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            let url = `/stock_movements?page=${currentPage}&itemsPerPage=${itemsPerPage}&order_by=${sorting.field}&order_direction=${sorting.direction}`;
            if (filters.variantId) url += `&variantId=${filters.variantId}`;
            if (searchTerm) url += `&product_name=${encodeURIComponent(searchTerm)}`;
            if (filters.category !== 'all') url += `&category=${encodeURIComponent(filters.category)}`;
            if (filters.brand !== 'all') url += `&brand=${encodeURIComponent(filters.brand)}`;
            if (filters.status !== 'all') url += `&product_status=${filters.status}`;
            if (filters.movementType !== 'all') url += `&type=${filters.movementType}`;
            if (filters.startDate) url += `&date_start=${filters.startDate}`;
            if (filters.endDate) url += `&date_end=${filters.endDate}`;
            if (filters.stockAlert) url += `&stock_alert=${filters.stockAlert}`;

            const response = await api.get(url, {headers: {'X-account-id': accountId.id}});
            setStockItems(response.data.data || []);
            setStockSummary(response.data.summary || null);
            if (response.data.pagination) {
                setTotalPages(response.data.pagination.totalPages);
                setTotalItems(response.data.pagination.totalItems);
            }
        } catch (error) {
            console.error("Error fetching stock items:", error);
        } finally {
            setIsLoading(false);
        }
    };


    const fetchProviders = async () => {
        setIsLoading(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/providers', {headers: {'X-account-id': accountId.id}});
            setProviders(response.data.data || []);
        } catch (error) {
            console.error("Error fetching providers:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPurchaseOrders = async () => {
        setPoLoading(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const params = new URLSearchParams({
                page: poPage,
                itemsPerPage: poItemsPerPage,
                status: poFilters.status,
                provider_id: poFilters.provider_id,
                date_start: poFilters.date_start,
                date_end: poFilters.date_end,
            });
            const response = await api.get(`/purchase-orders?${params.toString()}`, {
                headers: {'X-account-id': accountId.id}
            });
            setPurchaseOrders(response.data.data || []);
            if (response.data.pagination) {
                setPoTotalPages(response.data.pagination.totalPages);
                setPoTotalItems(response.data.pagination.totalItems);
            }
        } catch (error) {
            console.error("Error fetching purchase orders:", error);
        } finally {
            setPoLoading(false);
        }
    };

    const getProviderName = (providerUrl) => {
        if (!providerUrl) return '-';
        const id = typeof providerUrl === 'string' ? providerUrl.split('/').pop() : null;
        if (!id) return '-';
        const found = providers.find(p => p.id === id);
        return found ? found.name : id.substring(0, 8) + '…';
    };

    const fetchStockHistory = async (productId, variantId = null) => {
        setIsLoading(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get(`/stock/history?page=1&itemsPerPage=20${variantId ? `&variantId=${variantId}&item_type=variant` : `&productId=${productId}&item_type=product`}`, {
                headers: {'X-account-id': accountId.id}
            });
            setStockHistory(response.data);
            setShowHistoryModal(true);
        } catch (error) {
            console.error("Error fetching stock history:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Handlers ───────────────────────────────────────────────────────────

    const handleQuickAdjustment = async (product, type) => {
        if (parentHasVariants(product)) return;
        const quantity = type === 'increase' ? 1 : -1;
        // Optimistic: update the row right away, roll back if the API refuses
        const applyDelta = (delta) => setStockItems(prev => prev.map(i => {
            if (i.id !== product.id || i.type !== product.type) return i;
            const currentStock = i.currentStock + delta;
            return {...i, currentStock, isLowStock: i.isStockable && currentStock <= (i.minStock || 0)};
        }));
        applyDelta(quantity);
        setQuickAdjustError(null);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const formData = new FormData();
            if (isVariant(product)) formData.append('variantId', product.id);
            else formData.append('productId', product.id);
            formData.append('item_type', isVariant(product) ? 'variant' : 'product');
            formData.append('quantity', quantity);
            formData.append('reason', type === 'increase' ? 'Ajout rapide' : 'Retrait rapide');
            formData.append('type', type === 'increase' ? 'receipt' : 'issue');

            const response = await api.post('/stock/adjust', formData, {
                headers: {'X-account-id': accountId.id, 'Content-Type': 'multipart/form-data'}
            });
            if (!response.data.success) throw new Error(response.data.message);
            fetchStockItems({silent: true});
        } catch (error) {
            console.error("Error adjusting stock:", error);
            applyDelta(-quantity);
            setQuickAdjustError(`${product.name} : ${error.response?.data?.message || error.message || 'ajustement impossible'}`);
        }
    };

    const handleOpenAdjustModal = (product) => {
        if (parentHasVariants(product)) return;
        setAdjustmentData({
            productId: isVariant(product) ? '' : product.id,
            variantId: isVariant(product) ? product.id : '',
            productName: product.name,
            currentStock: product.currentStock,
            quantity: 1,
            reason: '',
            type: 'adjustment',
            // 'delta': add/remove a quantity — 'set': type the counted stock, the gap is computed
            mode: 'delta'
        });
        setShowAdjustModal(true);
        setAdjustmentMessage(null);
    };

    const setAdjustMode = (mode) => setAdjustmentData(prev => ({
        ...prev, mode, quantity: mode === 'set' ? prev.currentStock : 1
    }));

    // direction: 'in' | 'out' | 'set'
    const submitStockAdjustment = async (direction) => {
        let quantity;
        let defaultReason;
        if (direction === 'set') {
            const target = Number(adjustmentData.quantity);
            if (adjustmentData.quantity === '' || Number.isNaN(target)) {
                setAdjustmentMessage({type: 'error', text: 'Veuillez saisir le stock compté'});
                return;
            }
            quantity = target - adjustmentData.currentStock;
            if (quantity === 0) {
                setAdjustmentMessage({type: 'error', text: `Le stock est déjà à ${target}`});
                return;
            }
            defaultReason = `Correction de stock (${adjustmentData.currentStock} → ${target})`;
        } else {
            const amount = Number(adjustmentData.quantity);
            if (!amount || amount <= 0) {
                setAdjustmentMessage({type: 'error', text: 'Veuillez saisir une quantité valide'});
                return;
            }
            quantity = direction === 'in' ? amount : -amount;
            defaultReason = direction === 'in' ? 'Entrée de stock' : 'Sortie de stock';
        }
        const movementType = direction === 'set' ? 'adjustment' : direction === 'in' ? 'receipt' : 'issue';
        setIsAdjusting(true);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const formData = new FormData();
            if (adjustmentData.variantId) formData.append('variantId', adjustmentData.variantId);
            else formData.append('productId', adjustmentData.productId);
            formData.append('item_type', adjustmentData.variantId ? 'variant' : 'product');
            formData.append('quantity', quantity);
            formData.append('reason', adjustmentData.reason || defaultReason);
            formData.append('type', movementType);

            const response = await api.post('/stock/adjust', formData, {
                headers: {'X-account-id': accountId.id, 'Content-Type': 'multipart/form-data'}
            });

            if (response.data.success) {
                setAdjustmentMessage({type: 'success', text: response.data.message});
                setTimeout(() => {
                    fetchStockItems({silent: true});
                    setShowAdjustModal(false);
                }, 1500);
            } else {
                setAdjustmentMessage({type: 'error', text: response.data.message || 'Une erreur est survenue'});
            }
        } catch (error) {
            setAdjustmentMessage({type: 'error', text: error.response?.data?.message || 'Une erreur est survenue'});
        } finally {
            setIsAdjusting(false);
        }
    };

    const handleViewHistory = (item) => {
        fetchStockHistory(isVariant(item) ? null : item.id, isVariant(item) ? item.id : null);
    };

    const handleFilterChange = (e) => {
        const {name, value} = e.target;
        setFilters(prev => ({...prev, [name]: value}));
    };

    const resetFilters = () => {
        setFilters({
            category: 'all', brand: 'all', minStock: '', maxStock: '', status: 'all',
            startDate: '', endDate: '', movementType: 'all', variantId: '', productName: '', stockAlert: ''
        });
        setSelectedFilterCategory(null);
        setSelectedFilterBrand(null);
    };

    const applyFilters = () => {
        setCurrentPage(1);
        setShowFilterModal(false);
        fetchStockItems();
    };

    const activeFilterCount = useMemo(() =>
            Object.entries(filters).filter(([key, value]) => value !== 'all' && value !== '' && key !== 'variantId').length,
        [filters]
    );

    // Provider handlers
    const handleOpenProviderModal = (provider = null) => {
        if (provider) {
            setIsProviderModalEdit(true);
            setSelectedProvider(provider);
            setProviderForm({
                name: provider.name || '',
                email: provider.email || '',
                phone: provider.phone || '',
                address: provider.address || '',
                city: provider.city || '',
                country: provider.country || '',
                notes: provider.notes || ''
            });
        } else {
            setIsProviderModalEdit(false);
            setSelectedProvider(null);
            setProviderForm({
                name: '', email: '', phone: '', address: '', city: '', country: '', notes: ''
            });
        }
        setShowProviderModal(true);
    };

    const handleSaveProvider = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const formData = new FormData();
            Object.keys(providerForm).forEach(key => formData.append(key, providerForm[key]));

            if (isProviderModalEdit && selectedProvider) {
                await api.put(`/providers/${selectedProvider.id}`, formData, {
                    headers: {'X-account-id': accountId.id, 'Content-Type': 'multipart/form-data'}
                });
            } else {
                await api.post('/providers', formData, {
                    headers: {'X-account-id': accountId.id, 'Content-Type': 'multipart/form-data'}
                });
            }
            setShowProviderModal(false);
            fetchProviders();
        } catch (error) {
            console.error("Error saving provider:", error);
        }
    };

    const handleDeleteProvider = async (providerId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fournisseur ?')) return;
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            await api.delete(`/providers/${providerId}`, {headers: {'X-account-id': accountId.id}});
            fetchProviders();
        } catch (error) {
            console.error("Error deleting provider:", error);
        }
    };

    // Export handlers
    const handleOpenExportModal = () => {
        setExportFilters({
            category: '',
            brand: '',
            name: '',
            sku: '',
            stock_status: '',
            critical_only: false,
            stock_min: '',
            stock_max: '',
        });
        setSelectedExportCategory(null);
        setSelectedExportBrand(null);
        setExportMessage(null);
        setShowExportModal(true);
    };

    const handleExportStock = async () => {
        setIsExporting(true);
        setExportMessage(null);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Build query params
            const params = new URLSearchParams();
            if (exportFilters.category) params.append('category', exportFilters.category);
            if (exportFilters.brand) params.append('brand', exportFilters.brand);
            if (exportFilters.name) params.append('name', exportFilters.name);
            if (exportFilters.sku) params.append('sku', exportFilters.sku);
            if (exportFilters.stock_status) params.append('stock_status', exportFilters.stock_status);
            if (exportFilters.critical_only) params.append('critical_only', '1');
            if (exportFilters.stock_min) params.append('stock_min', exportFilters.stock_min);
            if (exportFilters.stock_max) params.append('stock_max', exportFilters.stock_max);

            const url = `/stock/export-excel${params.toString() ? '?' + params.toString() : ''}`;

            const response = await api.get(url, {
                headers: {'X-account-id': accountId.id},
                responseType: 'blob'
            });

            // Get filename from Content-Disposition header or generate one
            const contentDisposition = response.headers['content-disposition'];
            let filename = `stock_export_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match && match[1]) {
                    filename = match[1].replace(/['"]/g, '');
                }
            }

            // Create blob and download
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            // Get total items from header
            const totalItems = response.headers['x-total-items'];
            setExportMessage({
                type: 'success',
                text: `Export réussi ! ${totalItems ? `(${totalItems} produits)` : ''}`
            });

            // Close modal after short delay
            setTimeout(() => setShowExportModal(false), 1500);
        } catch (error) {
            console.error("Error exporting stock:", error);
            setExportMessage({
                type: 'error',
                text: error.response?.data?.error || 'Erreur lors de l\'exportation'
            });
        } finally {
            setIsExporting(false);
        }
    };

    // Stock valuation report handlers (GET /stock/report — returns a .pdf binary, no JSON preview)
    const handleOpenReportModal = () => {
        setReportFilters({category: '', product: '', dateStart: '', dateEnd: ''});
        setSelectedReportCategory(null);
        setSelectedReportProduct(null);
        setReportMessage(null);
        setShowReportModal(true);
    };

    const handleGenerateStockReport = async () => {
        setIsGeneratingReport(true);
        setReportMessage(null);
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const params = new URLSearchParams();
            if (reportFilters.category) params.append('category', reportFilters.category);
            if (reportFilters.product) params.append('product', reportFilters.product);
            if (reportFilters.dateStart) params.append('date_start', reportFilters.dateStart);
            if (reportFilters.dateEnd) params.append('date_end', reportFilters.dateEnd);

            const url = `/stock/report${params.toString() ? '?' + params.toString() : ''}`;

            const response = await api.get(url, {
                headers: {'X-account-id': accountId.id},
                responseType: 'blob'
            });

            const contentDisposition = response.headers['content-disposition'];
            let filename = `STOCK-${moment().format('YYYYMMDD_HHmmss')}.pdf`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match && match[1]) {
                    filename = match[1].replace(/['"]/g, '');
                }
            }

            const blob = new Blob([response.data], {
                type: 'application/pdf'
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            setReportMessage({type: 'success', text: 'Rapport généré avec succès'});
            setTimeout(() => setShowReportModal(false), 1500);
        } catch (error) {
            console.error("Error generating stock report:", error);
            // responseType is 'blob', so an error body also arrives as a Blob — decode it to read the API's message
            let errorText = 'Erreur lors de la génération du rapport';
            if (error.response?.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    errorText = JSON.parse(text)?.error || errorText;
                } catch (_) { /* keep default message */ }
            } else if (error.response?.data?.error) {
                errorText = error.response.data.error;
            }
            setReportMessage({type: 'error', text: errorText});
        } finally {
            setIsGeneratingReport(false);
        }
    };

    // Import handlers
    const handleOpenImportModal = () => {
        setImportFile(null);
        setImportResult(null);
        setShowImportModal(true);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file extension
            const ext = file.name.split('.').pop().toLowerCase();
            if (ext !== 'xlsx') {
                setImportResult({
                    type: 'error',
                    message: 'Seul le format .xlsx est accepté'
                });
                return;
            }
            setImportFile(file);
            setImportResult(null);
        }
    };

    const handleImportStock = async () => {
        if (!importFile) {
            setImportResult({
                type: 'error',
                message: 'Veuillez sélectionner un fichier Excel (.xlsx)'
            });
            return;
        }

        setIsImporting(true);
        setImportResult(null);

        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            const formData = new FormData();
            formData.append('file', importFile);

            const response = await api.post('/stock/import-excel', formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Content-Type': 'multipart/form-data'
                }
            });

            setImportResult({
                type: 'success',
                message: response.data.message || 'Import réussi',
                statistics: response.data.statistics,
                importReference: response.data.import_reference,
                totalProcessed: response.data.total_processed,
                errors: response.data.errors
            });

            // Refresh stock items after successful import
            setTimeout(() => {
                fetchStockItems();
            }, 1000);
        } catch (error) {
            console.error("Error importing stock:", error);
            setImportResult({
                type: 'error',
                message: error.response?.data?.error || 'Erreur lors de l\'importation',
                errors: error.response?.data?.errors
            });
        } finally {
            setIsImporting(false);
        }
    };

    // ─── Effects ────────────────────────────────────────────────────────────
    // Prevent double-fetch when searchTerm resets page to 1
    const skipNextFetch = useRef(false);


    useEffect(() => {
        if (activeTab !== 'inventory') return;
        if (skipNextFetch.current) {
            skipNextFetch.current = false;
            return;
        }
        fetchStockItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage, filters, sorting, activeTab]);

    useEffect(() => {
        if (activeTab === 'providers') fetchProviders();
        if (activeTab === 'history') fetchPurchaseOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'history') fetchPurchaseOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [poPage, poItemsPerPage]);

    // Purchase order filters apply as soon as they change (catalogue behaviour)
    const poFiltersMounted = useRef(false);
    useEffect(() => {
        if (!poFiltersMounted.current) {
            poFiltersMounted.current = true;
            return;
        }
        if (activeTab !== 'history') return;
        if (poPage !== 1) setPoPage(1); // the page effect above refetches
        else fetchPurchaseOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [poFilters]);

    useEffect(() => {
        const timer = setTimeout(() => {
            // setCurrentPage(1) only re-runs the effect above when the page actually changes;
            // flagging the skip otherwise would swallow the next pagination click
            if (currentPage !== 1) {
                skipNextFetch.current = true;
                setCurrentPage(1);
            }
            fetchStockItems();
        }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    // ─── Statistics ─────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        if (stockSummary) {
            return {
                totalProducts: stockSummary.total, inStock: stockSummary.inStock,
                lowStock: stockSummary.lowStock, outOfStock: stockSummary.outOfStock
            };
        }
        // Fallback (older API): current page only, variant group headers excluded,
        // each row counted in exactly one status
        const rows = stockItems.filter(item => !parentHasVariants(item));
        const outOfStock = rows.filter(item => item.currentStock <= 0).length;
        const lowStock = rows.filter(item => item.currentStock > 0 && item.isLowStock).length;
        return {totalProducts: rows.length, inStock: rows.length - lowStock - outOfStock, lowStock, outOfStock};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stockItems, stockSummary]);

    // Stat cards double as status filters; clicking the active one clears it
    const toggleStatusFilter = (status) => {
        setCurrentPage(1);
        setFilters(prev => ({...prev, status: prev.status === status ? 'all' : status}));
    };


    // ─── Modals ─────────────────────────────────────────────────────────────

    // RefSelector-style button for filter modal
    const FilterRefSelector = ({label, value, placeholder, onClick, onClear}) => (
        <div>
            <label className={fieldLabelClass}>{label} :</label>
            <button
                type="button"
                onClick={onClick}
                className={`w-full flex items-center justify-between px-3 sm:px-4 py-2 sm:py-2.5 text-sm border rounded-lg transition-colors text-left bg-white dark:bg-gray-800 ${
                    value
                        ? 'border-gray-300 dark:border-gray-600'
                        : 'border-dashed border-gray-300 dark:border-gray-600 hover:border-primary/50'
                }`}
            >
                {value ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        {value.color && (
                            <span className="w-5 h-5 rounded-md flex-shrink-0" style={{backgroundColor: value.color}}/>
                        )}
                        <span className="text-gray-800 dark:text-gray-100 font-medium truncate">
                            {value.name || '—'}
                        </span>
                    </div>
                ) : (
                    <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>
                )}
                <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    {value && (
                        <span onClick={(e) => { e.stopPropagation(); onClear(); }}
                              className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer">
                            <X className="w-3.5 h-3.5"/>
                        </span>
                    )}
                    <Plus className="w-4 h-4 text-gray-400 dark:text-gray-500"/>
                </div>
            </button>
        </div>
    );

    const FilterModal = () => (
        <Modal title="Filtres avancés" subtitle="Affinez vos résultats" onClose={() => setShowFilterModal(false)}
               footer={<ModalFooter cancelLabel="Réinitialiser" onCancel={resetFilters} onConfirm={applyFilters} label="Appliquer"/>}>
            <div className="space-y-4">
                <FilterRefSelector
                    label="Catégorie"
                    value={selectedFilterCategory}
                    placeholder="Sélectionner une catégorie"
                    onClick={() => setActiveFilterPicker('category')}
                    onClear={() => {
                        setSelectedFilterCategory(null);
                        setFilters(prev => ({...prev, category: 'all'}));
                    }}
                />
                <FilterRefSelector
                    label="Marque"
                    value={selectedFilterBrand}
                    placeholder="Sélectionner une marque"
                    onClick={() => setActiveFilterPicker('brand')}
                    onClear={() => {
                        setSelectedFilterBrand(null);
                        setFilters(prev => ({...prev, brand: 'all'}));
                    }}
                />
                <div>
                    <label className={fieldLabelClass}>Statut :</label>
                    <AppleSelect name="status" value={filters.status} onChange={handleFilterChange} className={providerFieldClass}>
                        <option value="all">Tous les statuts</option>
                        <option value="in-stock">En stock</option>
                        <option value="low-stock">Stock bas</option>
                        <option value="out-of-stock">Rupture</option>
                    </AppleSelect>
                </div>
                <div>
                    <label className={fieldLabelClass}>Période :</label>
                    <div className="grid grid-cols-2 gap-3">
                        <DateInput name="startDate" value={filters.startDate} onChange={handleFilterChange}
                               aria-label="Date de début" className={providerFieldClass}/>
                        <DateInput name="endDate" value={filters.endDate} onChange={handleFilterChange}
                               aria-label="Date de fin" className={providerFieldClass}/>
                    </div>
                </div>
            </div>
        </Modal>
    );

    const AdjustStockModal = () => {
        const footerBtn = 'py-4 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 flex items-center justify-center gap-2';
        return (
            <Modal
                title="Ajuster le stock"
                subtitle={`${adjustmentData.variantId ? 'Variante' : 'Produit'} · ${adjustmentData.productName}`}
                onClose={() => setShowAdjustModal(false)}
                size="sm"
                bodyClass=""
                footer={adjustmentData.mode === 'set' ? (
                    <ModalFooter onCancel={() => setShowAdjustModal(false)} onConfirm={() => submitStockAdjustment('set')}
                                 saving={isAdjusting} label={isAdjusting ? 'Traitement...' : 'Enregistrer la correction'}/>
                ) : (
                    // « Annuler | Sortie | Entrée »
                    <div className="grid grid-cols-3 border-t border-gray-200 dark:border-gray-700">
                        <button type="button" onClick={() => setShowAdjustModal(false)} disabled={isAdjusting}
                                className={`${footerBtn} text-gray-700 dark:text-gray-300`}>
                            Annuler
                        </button>
                        <button type="button" onClick={() => submitStockAdjustment('out')} disabled={isAdjusting}
                                className={`${footerBtn} border-l border-gray-200 dark:border-gray-700 text-red-600 dark:text-red-400`}>
                            {isAdjusting ? <Loader className="w-4 h-4 animate-spin"/> : <Minus className="w-4 h-4"/>}
                            Sortie
                        </button>
                        <button type="button" onClick={() => submitStockAdjustment('in')} disabled={isAdjusting}
                                className={`${footerBtn} border-l border-gray-200 dark:border-gray-700 text-primary`}>
                            {isAdjusting ? <Loader className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>}
                            Entrée
                        </button>
                    </div>
                )}
            >
                <SummaryStrip items={[
                    {key: 'current', label: 'Stock actuel', value: adjustmentData.currentStock},
                    {key: 'kind', label: 'Type', value: adjustmentData.variantId ? 'Variante' : 'Produit',
                        color: adjustmentData.variantId ? 'text-purple-600 dark:text-purple-300' : 'text-primary-dark dark:text-primary'},
                ]}/>
                <div className="px-6 py-4 space-y-4">
                    {/* Mode: add/remove a quantity, or type the counted stock */}
                    <div className="grid grid-cols-2 gap-1 p-1 bg-gray-100 dark:bg-gray-900 rounded-lg">
                        {[
                            {id: 'delta', label: 'Entrée / Sortie'},
                            {id: 'set', label: 'Corriger le stock'},
                        ].map(m => (
                            <button key={m.id} type="button" onClick={() => setAdjustMode(m.id)}
                                    className={`py-2 rounded-md text-sm font-medium transition ${adjustmentData.mode === m.id
                                        ? 'bg-white dark:bg-gray-700 text-primary shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
                                {m.label}
                            </button>
                        ))}
                    </div>
                    <div>
                        <label className={fieldLabelClass}>
                            {adjustmentData.mode === 'set' ? 'Stock compté' : 'Quantité'} :
                        </label>
                        <div className="flex items-center justify-center gap-4 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <button type="button" onClick={() => setAdjustmentData(prev => ({
                                ...prev,
                                quantity: Math.max(prev.mode === 'set' ? 0 : 1, (Number(prev.quantity) || 0) - 1)
                            }))}
                                    aria-label="Diminuer"
                                    className="w-10 h-10 flex items-center justify-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary hover:text-primary text-gray-600 dark:text-gray-400 transition">
                                <Minus className="w-4 h-4"/>
                            </button>
                            <div className="text-center min-w-[80px]">
                                <input type="number" inputMode="decimal" min={adjustmentData.mode === 'set' ? 0 : 1} step="any"
                                       value={adjustmentData.quantity}
                                       onChange={(e) => setAdjustmentData(prev => ({...prev, quantity: e.target.value}))}
                                       onFocus={(e) => e.target.select()}
                                       onKeyDown={(e) => {
                                           if (e.key === 'Enter' && adjustmentData.mode === 'set') submitStockAdjustment('set');
                                       }}
                                       aria-label={adjustmentData.mode === 'set' ? 'Stock compté' : 'Quantité'}
                                       className="w-28 text-center text-3xl font-bold tabular-nums text-gray-900 dark:text-gray-100 bg-transparent border-b-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-primary focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"/>
                                <div className="text-xs text-gray-500 dark:text-gray-400">unités</div>
                            </div>
                            <button type="button" onClick={() => setAdjustmentData(prev => ({...prev, quantity: (Number(prev.quantity) || 0) + 1}))}
                                    aria-label="Augmenter"
                                    className="w-10 h-10 flex items-center justify-center bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary hover:text-primary text-gray-600 dark:text-gray-400 transition">
                                <Plus className="w-4 h-4"/>
                            </button>
                        </div>
                        {/* Preview of the resulting stock */}
                        {(() => {
                            const value = Number(adjustmentData.quantity);
                            if (adjustmentData.quantity === '' || Number.isNaN(value)) return null;
                            const current = adjustmentData.currentStock;
                            if (adjustmentData.mode === 'set') {
                                const gap = value - current;
                                return (
                                    <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2 tabular-nums">
                                        {current} → <span className="font-semibold text-gray-700 dark:text-gray-200">{value}</span>
                                        {' · '}écart{' '}
                                        <span className={`font-semibold ${gap > 0 ? 'text-green-600 dark:text-green-400' : gap < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                                            {gap > 0 ? `+${gap}` : gap}
                                        </span>
                                    </p>
                                );
                            }
                            return (
                                <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2 tabular-nums">
                                    Après sortie : <span className="font-semibold text-red-600 dark:text-red-400">{current - value}</span>
                                    {' · '}après entrée : <span className="font-semibold text-green-600 dark:text-green-400">{current + value}</span>
                                </p>
                            );
                        })()}
                    </div>
                    <div>
                        <label className={fieldLabelClass}>Motif (optionnel) :</label>
                        <textarea value={adjustmentData.reason}
                                  onChange={(e) => setAdjustmentData(prev => ({...prev, reason: e.target.value}))}
                                  rows="2" placeholder="Raison de l'ajustement..."
                                  className={providerFieldClass}/>
                    </div>
                    <ModalMessage message={adjustmentMessage}/>
                </div>
            </Modal>
        );
    };

    const StockHistoryModal = () => {
        // "Ventes uniquement" isolates the movements caused by a POS sale (see
        // parseSaleReference / MOVEMENT_TYPE_CONFIG.isSale) so it's obvious which
        // decrements came from a sale vs. a reception/adjustment/etc.
        const [salesOnly, setSalesOnly] = useState(false);
        // "Regrouper par type" buckets the same list by action type instead of a
        // flat chronological feed.
        const [groupByType, setGroupByType] = useState(false);
        const [collapsedTypes, setCollapsedTypes] = useState({});

        const allMovements = stockHistory?.movements ?? [];

        const summary = useMemo(() => allMovements.reduce(
            (acc, m) => {
                if (m.newStock > m.previousStock) acc.totalIn += m.quantity;
                else acc.totalOut += m.quantity;
                if (getMovementTypeConfig(m.type).isSale) {
                    acc.salesCount += 1;
                    acc.salesOut += Math.abs(m.quantity);
                }
                return acc;
            },
            {totalIn: 0, totalOut: 0, salesCount: 0, salesOut: 0}
        ), [allMovements]);

        const visibleMovements = salesOnly
            ? allMovements.filter(m => getMovementTypeConfig(m.type).isSale)
            : allMovements;

        // Grouped by type, busiest action first (by total |quantity| moved).
        const typeGroups = useMemo(() => {
            if (!groupByType) return null;
            const byType = new Map();
            visibleMovements.forEach(m => {
                if (!byType.has(m.type)) byType.set(m.type, []);
                byType.get(m.type).push(m);
            });
            return Array.from(byType.entries())
                .map(([type, items]) => ({
                    type,
                    items,
                    totalQty: items.reduce((s, m) => s + Math.abs(m.quantity), 0),
                }))
                .sort((a, b) => b.totalQty - a.totalQty);
        }, [groupByType, visibleMovements]);

        const toggleTypeCollapsed = (type) =>
            setCollapsedTypes(prev => ({...prev, [type]: !prev[type]}));

        // Flat row, split from the next one by a hairline (catalogue mobile list)
        const renderMovementCard = (movement) => {
            const isIn = movement.newStock > movement.previousStock;
            const delta = movement.newStock - movement.previousStock;
            const actor = movement.user?.email || movement.employee?.name || null;
            const typeCfg = getMovementTypeConfig(movement.type);
            const saleShortId = typeCfg.isSale ? parseSaleReference(movement.reference) : null;

            return (
                <div key={movement.id} className="flex items-start gap-3 px-6 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isIn ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                        {isIn
                            ? <ArrowDown className="w-4 h-4 text-green-600 dark:text-green-400"/>
                            : <ArrowUp className="w-4 h-4 text-red-600 dark:text-red-400"/>
                        }
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                {isIn ? 'Entrée' : 'Sortie'}
                            </span>
                            <MovementTypeBadge type={movement.type}/>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                            {moment(movement.createdAt).format('DD MMM YYYY • HH:mm')}
                            {' · '}
                            <span className={movement.previousStock < 0 ? 'text-red-500 dark:text-red-400' : ''}>{movement.previousStock}</span>
                            <ArrowRight className="inline w-3 h-3 mx-1 -mt-0.5"/>
                            <span className={`font-medium ${movement.newStock < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'}`}>{movement.newStock}</span>
                        </p>
                        {/* Sale pointer: which sale caused this decrement */}
                        {saleShortId && (
                            <p className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                                <ShoppingCart className="w-3.5 h-3.5 flex-shrink-0"/>
                                Décrémenté par la vente <span className="font-mono font-semibold">#{saleShortId}</span>
                            </p>
                        )}
                        {(movement.reference || actor) && (
                            <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500 dark:text-gray-400">
                                {movement.reference && (
                                    <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-600 dark:text-gray-400 truncate max-w-full">
                                        {movement.reference}
                                    </span>
                                )}
                                {actor && (
                                    <span className="flex items-center gap-1">
                                        <User className="w-3 h-3"/> {actor}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <span className={`text-lg font-bold tabular-nums flex-shrink-0 ${isIn ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                        {delta > 0 ? '+' : ''}{delta}
                    </span>
                </div>
            );
        };

        const toggleBtn = (active, activeCls) => `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
            active ? activeCls : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary/30 hover:text-primary'}`;

        return (
            <Modal
                title={stockHistory?.item?.name || 'Historique'}
                subtitle="Historique des mouvements"
                onClose={() => setShowHistoryModal(false)}
                size="lg"
                bodyClass=""
                footer={
                    <button type="button" onClick={() => setShowHistoryModal(false)}
                            className="w-full py-4 border-t border-gray-200 dark:border-gray-700 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                        Fermer
                    </button>
                }
            >
                {stockHistory ? (
                    <>
                        {/* Totaux : bande à filets ; « Ventes » filtre la liste */}
                        <SummaryStrip
                            active={salesOnly ? 'sales' : null}
                            onSelect={(key) => setSalesOnly(v => key === 'sales' ? !v : false)}
                            items={[
                                {
                                    key: 'current', label: 'Stock actuel', value: stockHistory.item.currentStock,
                                    hint: stockHistory.item.isLowStock ? 'Stock bas' : null,
                                    color: stockHistory.item.currentStock < 0 ? 'text-red-600 dark:text-red-400'
                                        : stockHistory.item.isLowStock ? 'text-amber-600 dark:text-amber-400' : undefined,
                                },
                                {key: 'in', label: 'Entrées', value: `+${summary.totalIn}`, color: 'text-green-600 dark:text-green-400'},
                                {key: 'out', label: 'Sorties', value: `-${summary.totalOut}`, color: 'text-red-600 dark:text-red-400'},
                                {key: 'sales', label: 'Ventes', value: summary.salesCount},
                            ]}
                        />

                        {/* Toolbar: filter + group toggle */}
                        {allMovements.length > 0 && (
                            <div className="flex items-center justify-between gap-3 px-6 py-2.5 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-2">
                                    <button onClick={() => setSalesOnly(v => !v)}
                                            className={toggleBtn(salesOnly, 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400')}>
                                        <ShoppingCart className="w-3.5 h-3.5"/>
                                        Ventes uniquement
                                    </button>
                                    {salesOnly && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">{visibleMovements.length} mouvement{visibleMovements.length > 1 ? 's' : ''}</span>
                                    )}
                                </div>
                                <button onClick={() => setGroupByType(v => !v)}
                                        className={toggleBtn(groupByType, 'bg-primary/10 dark:bg-primary/20 border-primary/30 text-primary-dark dark:text-primary')}>
                                    <Layers className="w-3.5 h-3.5"/>
                                    Regrouper par type
                                </button>
                            </div>
                        )}

                        {visibleMovements.length > 0 ? (
                            groupByType ? (
                                <div>
                                    {typeGroups.map(({type, items, totalQty}) => {
                                        const cfg = getMovementTypeConfig(type);
                                        const Icon = cfg.icon;
                                        const collapsed = collapsedTypes[type];
                                        return (
                                            <div key={type} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                                                <button
                                                    onClick={() => toggleTypeCollapsed(type)}
                                                    className="w-full flex items-center justify-between gap-2 px-6 py-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                                                >
                                                    <span className="flex items-center gap-2 min-w-0">
                                                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg}`}>
                                                            <Icon className={`w-3.5 h-3.5 ${cfg.text}`}/>
                                                        </span>
                                                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">{cfg.label}</span>
                                                        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                            {items.length} mouvement{items.length > 1 ? 's' : ''} · {totalQty} unité{totalQty > 1 ? 's' : ''}
                                                        </span>
                                                    </span>
                                                    <ChevronRight className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform flex-shrink-0 ${collapsed ? '' : 'rotate-90'}`}/>
                                                </button>
                                                {!collapsed && (
                                                    <div className="divide-y divide-gray-200 dark:divide-gray-700 border-t border-gray-200 dark:border-gray-700">
                                                        {items.map(renderMovementCard)}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {visibleMovements.map(renderMovementCard)}
                                </div>
                            )
                        ) : (
                            <ListEmpty
                                icon={History}
                                title={salesOnly ? 'Aucune vente n\'a décrémenté ce stock' : 'Aucun mouvement'}
                                text={salesOnly
                                    ? 'Essayez de retirer le filtre "Ventes uniquement".'
                                    : 'Aucun mouvement enregistré pour cet article.'}
                            />
                        )}
                    </>
                ) : (
                    <div className="flex justify-center items-center py-12">
                        <Loader className="w-8 h-8 text-primary animate-spin"/>
                    </div>
                )}
            </Modal>
        );
    };

    // ─── Product Card (Mobile) ──────────────────────────────────────────────
    // Same row as the catalogue mobile list (mobileListClass): no margin, no
    // gap, no left accent; a variant shows its « ↳ produit » line instead.
    const ProductCard = ({item}) => (
        <div className={`p-4 transition-colors ${
            item.currentStock <= 0 && !parentHasVariants(item)
                ? 'bg-red-50 dark:bg-red-900/20'
                : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'
        }`}>
            <div className="flex items-start gap-3 mb-3">
                <ItemAvatar item={item} size={isVariant(item) ? 'md' : 'lg'}/>
                <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{item.name}</h3>
                    {isVariant(item) && item.productName && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">↳ {item.productName}</p>
                    )}
                    {item.barcode && (
                        <p className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                            <span className="font-mono">{item.barcode}</span>
                        </p>
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {!parentHasVariants(item) && <StatusBadge isLowStock={item.isLowStock} currentStock={item.currentStock}/>}
                        <TypeBadge item={item}/>
                        {!isVariant(item) && <CategoryChip name={item.category} color={item.categoryColor}/>}
                    </div>
                </div>
                {/* Actions in a « ⋯ » menu, like the catalogue cards */}
                {!parentHasVariants(item) ? (
                    <RowActions label={`Actions pour ${item.name}`} actions={[
                        {label: 'Ajuster le stock', icon: Edit3, onClick: () => handleOpenAdjustModal(item)},
                        {label: 'Historique', icon: History, onClick: () => handleViewHistory(item)},
                        {divider: true},
                        {label: 'Ajouter 1', icon: Plus, onClick: () => handleQuickAdjustment(item, 'increase')},
                        {label: 'Retirer 1', icon: Minus, onClick: () => handleQuickAdjustment(item, 'decrease')},
                    ]}/>
                ) : item.hasVariants && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        Total <span className="font-semibold text-gray-700 dark:text-gray-200">{item.currentStock}</span>
                    </span>
                )}
            </div>

            {!parentHasVariants(item) && (<>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                        <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Stock actuel</span>
                        <div className={`text-xl font-bold ${item.isLowStock ? 'text-amber-600 dark:text-amber-400' : item.currentStock <= 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                            {item.currentStock}
                        </div>
                        <StockBar current={item.currentStock} min={item.minStock}/>
                    </div>
                    <div>
                        <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Stock min</span>
                        <div className="font-semibold text-gray-700 dark:text-gray-300">{item.minStock || '—'}</div>
                    </div>
                </div>
            </>)}
        </div>
    );

    const statusLabel = {'in-stock': 'En stock', 'low-stock': 'Stock bas', 'out-of-stock': 'Rupture'};
    const clearAllFilters = () => {
        resetFilters();
        setSearchTerm('');
    };

    // Inventory tab: active filter chips
    const inventoryChips = [
        searchTerm && {key: 'search', label: `Recherche: ${searchTerm}`, onRemove: () => setSearchTerm('')},
        filters.category !== 'all' && {
            key: 'category', label: selectedFilterCategory?.name || filters.category,
            style: {backgroundColor: selectedFilterCategory?.color || '#4DB6AC'}, className: ' ',
            onRemove: () => {
                setFilters(prev => ({...prev, category: 'all'}));
                setSelectedFilterCategory(null);
            }
        },
        filters.brand !== 'all' && {
            key: 'brand', label: `Marque: ${selectedFilterBrand?.name || filters.brand}`, className: 'bg-purple-500',
            onRemove: () => {
                setFilters(prev => ({...prev, brand: 'all'}));
                setSelectedFilterBrand(null);
            }
        },
        filters.status !== 'all' && {
            key: 'status', label: `Statut: ${statusLabel[filters.status] || filters.status}`,
            className: filters.status === 'in-stock' ? 'bg-green-500' : filters.status === 'low-stock' ? 'bg-amber-500' : 'bg-red-500',
            onRemove: () => setFilters(prev => ({...prev, status: 'all'}))
        },
        filters.startDate && {key: 'start', label: `Du: ${filters.startDate}`, className: 'bg-gray-500', onRemove: () => setFilters(prev => ({...prev, startDate: ''}))},
        filters.endDate && {key: 'end', label: `Au: ${filters.endDate}`, className: 'bg-gray-500', onRemove: () => setFilters(prev => ({...prev, endDate: ''}))},
    ].filter(Boolean);

    // Purchase orders tab: active filter chips (dates only count when changed from the default range)
    const poStatusLabels = {draft: 'Brouillon', confirmed: 'Confirmé', received: 'Reçu', cancelled: 'Annulé', pending: 'En attente'};
    const poChips = [
        poFilters.status && {key: 'status', label: `Statut: ${poStatusLabels[poFilters.status] || poFilters.status}`, className: 'bg-teal-500', onRemove: () => setPoFilters(prev => ({...prev, status: ''}))},
        poFilters.provider_id && {key: 'provider', label: `Fournisseur: ${providers.find(p => p.id === poFilters.provider_id)?.name || '…'}`, className: 'bg-purple-500', onRemove: () => setPoFilters(prev => ({...prev, provider_id: ''}))},
        poFilters.date_start !== DEFAULT_PO_FILTERS.date_start && {key: 'start', label: `Du: ${poFilters.date_start}`, className: 'bg-gray-500', onRemove: () => setPoFilters(prev => ({...prev, date_start: DEFAULT_PO_FILTERS.date_start}))},
        poFilters.date_end !== DEFAULT_PO_FILTERS.date_end && {key: 'end', label: `Au: ${poFilters.date_end}`, className: 'bg-gray-500', onRemove: () => setPoFilters(prev => ({...prev, date_end: DEFAULT_PO_FILTERS.date_end}))},
    ].filter(Boolean);

    // Providers tab: client-side search (the list is not paginated)
    const providerTerm = providerSearch.trim().toLowerCase();
    const filteredProviders = providerTerm
        ? providers.filter(p => [p.name, p.email, p.phone, p.city].some(v => v && String(v).toLowerCase().includes(providerTerm)))
        : providers;

    const formatMad = (n) => `${(n || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2})} MAD`;

    // ─── Main Render ────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            {/* Header — same layout as the catalogue page */}
            <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <div className="px-4 py-4 sm:px-8">
                    <div className="page-header-row">
                        <h1 className="page-title">Stock</h1>
                        <div className="flex items-center gap-1 sm:gap-2">
                            {activeTab === 'inventory' && (<>
                                <HeaderAction icon={Upload} label="Importer" onClick={handleOpenImportModal}/>
                                <HeaderAction icon={Download} label="Exporter" onClick={handleOpenExportModal}/>
                                <HeaderAction icon={FileText} label="Rapport" onClick={handleOpenReportModal}/>
                                <HeaderAction icon={RefreshCw} title="Actualiser" onClick={() => fetchStockItems()}/>
                            </>)}
                            {activeTab === 'valuation' && (
                                <HeaderAction icon={Plus} label="Nouveau" onClick={() => setInventoryCreateKey(k => k + 1)}/>
                            )}
                            {activeTab === 'reception' && (<>
                                <HeaderAction icon={X} label="Vider" onClick={() => setReceptionResetKey(k => k + 1)}/>
                                <HeaderAction icon={Save} label="Enregistrer" onClick={() => setReceptionSubmitKey(k => k + 1)}/>
                            </>)}
                            {activeTab === 'history' && (<>
                                <HeaderAction icon={Plus} label="Nouveau" onClick={() => setActiveTab('reception')}/>
                                <HeaderAction icon={RefreshCw} title="Actualiser" onClick={() => fetchPurchaseOrders()}/>
                            </>)}
                            {activeTab === 'providers' && (
                                <HeaderAction icon={Plus} label="Nouveau" onClick={() => handleOpenProviderModal()}/>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex overflow-x-auto no-scrollbar px-2 sm:px-4 border-b border-gray-200 dark:border-gray-700 md:justify-center">
                    {[
                        {id: 'inventory', label: 'Historique', icon: History},
                        {id: 'valuation', label: 'Inventaire', icon: FileText},
                        {id: 'reception', label: 'Réception', icon: Truck},
                        {id: 'history', label: 'Bons de commande', icon: ClipboardList},
                        {id: 'providers', label: 'Fournisseurs', icon: Building2},
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={tabClass(activeTab === tab.id)}
                        >
                            <tab.icon className={tabIconClass}/>
                            {tab.label}
                        </button>
                    ))}
                </div>
            </header>

            {/* Content — lists are edge-to-edge like the catalogue, the reception form keeps its padding */}
            <div className={activeTab === 'reception' ? 'p-4 sm:p-6' : ''}>
                {activeTab === 'inventory' && (
                    <div className="max-w-full">
                        <div className="flex flex-col sm:flex-row">
                            <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher un produit ou un code-barres..."/>
                            <FilterButton count={activeFilterCount} onClick={() => setShowFilterModal(true)}/>
                        </div>
                        <ActiveFilters chips={inventoryChips} onClearAll={clearAllFilters}/>

                        {/* Status summary — doubles as a status filter */}
                        <SummaryStrip
                            active={filters.status}
                            onSelect={toggleStatusFilter}
                            items={[
                                {key: 'all', label: 'Total articles', value: stats.totalProducts},
                                {key: 'in-stock', label: 'En stock', value: stats.inStock, color: 'text-green-600 dark:text-green-400'},
                                {key: 'low-stock', label: 'Stock bas', value: stats.lowStock, color: 'text-amber-600 dark:text-amber-400'},
                                {key: 'out-of-stock', label: 'Rupture', value: stats.outOfStock, color: 'text-red-600 dark:text-red-400'},
                            ]}
                        />

                        {quickAdjustError && (
                            <div className="flex items-center justify-between gap-3 p-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
                                <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0"/>{quickAdjustError}</span>
                                <button onClick={() => setQuickAdjustError(null)} className="p-1 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg">
                                    <X className="w-4 h-4"/>
                                </button>
                            </div>
                        )}

                        {isLoading ? (
                            <ListLoader label="Chargement du stock..."/>
                        ) : (
                            <ListCard>
                                {stockItems.length === 0 ? (
                                    <ListEmpty icon={Package} title="Aucun article trouvé"
                                               text="Aucun article ne correspond à vos critères. Modifiez la recherche ou les filtres."/>
                                ) : (<>
                                    {/* Desktop Table */}
                                    <div className="hidden lg:block overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                                            <tr>
                                                <th className={thClass}>Produit</th>
                                                <th className={thClass}>Catégorie</th>
                                                <th className={thClass}>Stock</th>
                                                <th className={thClass}>Statut</th>
                                                <th className={`${thClass} text-center`}>Ajust. rapide</th>
                                                <th className={`${thClass} text-right`}>Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {visibleItems.map((item) => (
                                                <tr key={item.id} className={`transition-colors ${
                                                    item.currentStock <= 0 && !parentHasVariants(item)
                                                        ? 'bg-red-50/60 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20'
                                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                                }`}>
                                                    <td className={`px-6 py-4 ${isVariant(item) ? 'pl-14' : ''}`}>
                                                        <div className="flex items-center gap-3">
                                                            <ItemAvatar item={item} size={isVariant(item) ? 'sm' : 'md'}/>
                                                            <div className="min-w-0">
                                                                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate max-w-xs">{item.name}</div>
                                                                {isVariant(item) && item.productName && (
                                                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">↳ {item.productName}</div>
                                                                )}
                                                                {item.barcode && (
                                                                    <div className="mt-0.5 text-xs text-gray-400 dark:text-gray-500 font-mono">{item.barcode}</div>
                                                                )}
                                                                {(parentHasVariants(item) || isVariant(item)) && (
                                                                    <div className="mt-1"><TypeBadge item={item}/></div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {!isVariant(item) && (
                                                            <div className="flex flex-col items-start gap-1">
                                                                <CategoryChip name={item.category} color={item.categoryColor}/>
                                                                {item.brand && (
                                                                    <span className="text-xs text-gray-500 dark:text-gray-400">{item.brand}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 min-w-[130px]">
                                                        {!parentHasVariants(item) ? (<>
                                                            <div className="flex items-baseline gap-1">
                                                                <span className={`text-lg font-bold ${item.isLowStock ? 'text-amber-600 dark:text-amber-400' : item.currentStock <= 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                                                    {item.currentStock}
                                                                </span>
                                                                {item.minStock ? (
                                                                    <span className="text-xs text-gray-400 dark:text-gray-500">/ min {item.minStock}</span>
                                                                ) : null}
                                                            </div>
                                                            <StockBar current={item.currentStock} min={item.minStock}/>
                                                        </>) : item.hasVariants && (
                                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                Total <span className="font-semibold text-gray-800 dark:text-gray-200">{item.currentStock}</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {!parentHasVariants(item) && (
                                                            <StatusBadge isLowStock={item.isLowStock} currentStock={item.currentStock}/>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        {!parentHasVariants(item) && (
                                                            <div className="flex items-center justify-center gap-1">
                                                                <Button variant="secondary" size="sm" icon={Minus} title="Retirer 1"
                                                                        onClick={() => handleQuickAdjustment(item, 'decrease')}/>
                                                                <Button variant="tinted" size="sm" icon={Plus} title="Ajouter 1"
                                                                        onClick={() => handleQuickAdjustment(item, 'increase')}/>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        {!parentHasVariants(item) && (
                                                            <RowActions actions={[
                                                                {label: 'Ajuster le stock', icon: Edit3, onClick: () => handleOpenAdjustModal(item)},
                                                                {label: 'Historique', icon: History, tone: 'green', onClick: () => handleViewHistory(item)},
                                                            ]}/>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Cards */}
                                    <div className={mobileListClass}>
                                        {visibleItems.map((item) => (
                                            <ProductCard key={item.id} item={item}/>
                                        ))}
                                    </div>
                                </>)}
                            </ListCard>
                        )}

                        {!isLoading && stockItems.length > 0 && (
                            <Pagination
                                page={currentPage}
                                totalPages={totalPages}
                                onPage={setCurrentPage}
                                itemsPerPage={itemsPerPage}
                                onItemsPerPage={(n) => {
                                    setCurrentPage(1);
                                    setItemsPerPage(n);
                                }}
                                summary={`${totalItems} produits`}
                            />
                        )}
                    </div>
                )}

                {activeTab === 'valuation' && (
                    <InventairePage createRequestKey={inventoryCreateKey}/>
                )}

                {activeTab === 'reception' && (
                    <StockReception submitRequestKey={receptionSubmitKey} resetRequestKey={receptionResetKey}/>
                )}

                {/* ── Bons de commande / Purchase Orders ─────────────────── */}
                {activeTab === 'history' && (
                    <div className="max-w-full">
                        <div className="flex flex-col sm:flex-row">
                            <ToolbarSelect label="Statut" value={poFilters.status} className="flex-1"
                                           onChange={(v) => setPoFilters(prev => ({...prev, status: v}))}>
                                <option value="">Tous les statuts</option>
                                {Object.entries(poStatusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                            </ToolbarSelect>
                            <ToolbarSelect label="Fournisseur" value={poFilters.provider_id} className="flex-1"
                                           onChange={(v) => setPoFilters(prev => ({...prev, provider_id: v}))}>
                                <option value="">Tous les fournisseurs</option>
                                {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </ToolbarSelect>
                            <div className="flex">
                                <ToolbarDate label="Du" value={poFilters.date_start} max={poFilters.date_end} className="flex-1 sm:w-48"
                                             onChange={(v) => setPoFilters(prev => ({...prev, date_start: v}))}/>
                                <ToolbarDate label="Au" value={poFilters.date_end} min={poFilters.date_start} className="flex-1 sm:w-48"
                                             onChange={(v) => setPoFilters(prev => ({...prev, date_end: v}))}/>
                            </div>
                        </div>
                        <ActiveFilters chips={poChips} onClearAll={() => setPoFilters(DEFAULT_PO_FILTERS)}/>

                        <SummaryStrip items={[
                            {key: 'total', label: 'Total commandes', value: poTotalItems},
                            {key: 'confirmed', label: 'Confirmées (page)', value: purchaseOrders.filter(o => o.status === 'confirmed').length, color: 'text-blue-600 dark:text-blue-400'},
                            {key: 'received', label: 'Reçues (page)', value: purchaseOrders.filter(o => o.status === 'received').length, color: 'text-green-600 dark:text-green-400'},
                            {key: 'value', label: 'Valeur (page)', value: formatMad(purchaseOrders.reduce((s, o) => s + (o.totalCost || 0), 0)), color: 'text-primary'},
                        ]}/>

                        {poLoading ? (
                            <ListLoader label="Chargement des bons de commande..."/>
                        ) : (
                            <ListCard>
                                {purchaseOrders.length === 0 ? (
                                    <ListEmpty icon={ClipboardList} title="Aucun bon de commande"
                                               text="Aucune commande ne correspond aux filtres sélectionnés."/>
                                ) : (<>
                                    {/* Desktop table */}
                                    <div className="hidden lg:block overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                                            <tr>
                                                <th className={thClass}>Référence</th>
                                                <th className={thClass}>Statut</th>
                                                <th className={thClass}>Fournisseur</th>
                                                <th className={thClass}>Confirmé le</th>
                                                <th className={thClass}>Reçu le</th>
                                                <th className={`${thClass} text-center`}>Articles</th>
                                                <th className={`${thClass} text-right`}>Total</th>
                                                <th className="px-6 py-4 w-10"></th>
                                            </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {purchaseOrders.map((order) => (
                                                <React.Fragment key={order.id}>
                                                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                                        onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center gap-3">
                                                                <Avatar name={getProviderName(order.provider)}/>
                                                                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{order.reference}</div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap"><POStatusBadge status={order.status}/></td>
                                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{getProviderName(order.provider)}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                                            {order.confirmedAt ? moment(order.confirmedAt).format('DD MMM YYYY') : '—'}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                                            {order.receivedAt ? moment(order.receivedAt).format('DD MMM YYYY') : '—'}
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-xs font-semibold">
                                                                {order.items?.length ?? 0}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-gray-100">{formatMad(order.totalCost)}</td>
                                                        <td className="px-6 py-4 text-gray-400 dark:text-gray-500">
                                                            {expandedOrderId === order.id ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
                                                        </td>
                                                    </tr>
                                                    {expandedOrderId === order.id && order.items?.length > 0 && (
                                                        <tr>
                                                            <td colSpan={8} className="px-6 pb-4 pt-0 bg-gray-50 dark:bg-gray-900/40">
                                                                <div className="bg-white dark:bg-gray-800 shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700 mt-1">
                                                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                                                                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                                                                        <tr>
                                                                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Produit</th>
                                                                            <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Qté</th>
                                                                            <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Prix unitaire</th>
                                                                            <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Sous-total</th>
                                                                            <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Stock actuel</th>
                                                                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Note</th>
                                                                        </tr>
                                                                        </thead>
                                                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                                                        {order.items.map((item) => (
                                                                            <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                                                <td className="px-6 py-3 font-medium text-gray-900 dark:text-gray-100">{item.productName || '—'}</td>
                                                                                <td className="px-6 py-3 text-center font-semibold text-gray-800 dark:text-gray-200">{item.quantity}</td>
                                                                                <td className="px-6 py-3 text-right text-gray-600 dark:text-gray-400">{formatMad(item.unitCost)}</td>
                                                                                <td className="px-6 py-3 text-right font-semibold text-gray-900 dark:text-gray-100">{formatMad(item.lineTotal)}</td>
                                                                                <td className="px-6 py-3 text-center font-semibold text-gray-700 dark:text-gray-300">{item.currentStock ?? '—'}</td>
                                                                                <td className="px-6 py-3 text-gray-500 dark:text-gray-400 text-xs">{item.note || '—'}</td>
                                                                            </tr>
                                                                        ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile cards */}
                                    <div className={mobileListClass}>
                                        {purchaseOrders.map((order) => {
                                            const isExpanded = expandedOrderId === order.id;
                                            return (
                                                <div key={order.id} className={`${mobileRowClass} cursor-pointer`}
                                                     onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}>
                                                    <div className="flex items-start gap-3 mb-3">
                                                        <Avatar name={getProviderName(order.provider)} size="lg"/>
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{order.reference}</h3>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mb-2">{getProviderName(order.provider)}</p>
                                                            <POStatusBadge status={order.status}/>
                                                        </div>
                                                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400"/> : <ChevronDown className="w-4 h-4 text-gray-400"/>}
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                                        <div>
                                                            <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Reçu le</span>
                                                            <div className="font-semibold text-gray-900 dark:text-gray-100">{order.receivedAt ? moment(order.receivedAt).format('DD MMM YYYY') : '—'}</div>
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Total ({order.items?.length ?? 0} art.)</span>
                                                            <div className="font-semibold text-gray-900 dark:text-gray-100">{formatMad(order.totalCost)}</div>
                                                        </div>
                                                    </div>
                                                    {isExpanded && order.items?.length > 0 && (
                                                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 space-y-2">
                                                            {order.items.map((item) => (
                                                                <div key={item.id} className="flex justify-between gap-3 text-sm">
                                                                    <span className="text-gray-800 dark:text-gray-200 truncate">{item.quantity} × {item.productName || '—'}</span>
                                                                    <span className="font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">{formatMad(item.lineTotal)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>)}
                            </ListCard>
                        )}

                        {!poLoading && purchaseOrders.length > 0 && (
                            <Pagination
                                page={poPage}
                                totalPages={poTotalPages}
                                onPage={setPoPage}
                                itemsPerPage={poItemsPerPage}
                                onItemsPerPage={(n) => {
                                    setPoPage(1);
                                    setPoItemsPerPage(n);
                                }}
                                options={[10, 25, 50]}
                                summary={`${poTotalItems} commandes`}
                            />
                        )}
                    </div>
                )}

                {activeTab === 'providers' && (
                    <div className="max-w-full">
                        <div className="flex flex-col sm:flex-row">
                            <SearchInput value={providerSearch} onChange={setProviderSearch} placeholder="Rechercher un fournisseur (nom, email, téléphone, ville)..."/>
                        </div>
                        <ActiveFilters
                            chips={providerSearch ? [{key: 'search', label: `Recherche: ${providerSearch}`, onRemove: () => setProviderSearch('')}] : []}
                            onClearAll={() => setProviderSearch('')}
                        />

                        {isLoading ? (
                            <ListLoader label="Chargement des fournisseurs..."/>
                        ) : (
                            <ListCard>
                                {filteredProviders.length === 0 ? (
                                    <ListEmpty
                                        icon={Building2}
                                        title={providers.length === 0 ? 'Aucun fournisseur' : 'Aucun fournisseur trouvé'}
                                        text={providers.length === 0 ? 'Commencez par ajouter un nouveau fournisseur.' : 'Aucun fournisseur ne correspond à votre recherche.'}
                                        action={providers.length === 0 && (
                                            <Button variant="tinted" size="sm" icon={Plus} className="mt-4" onClick={() => handleOpenProviderModal()}>
                                                Ajouter un fournisseur
                                            </Button>
                                        )}
                                    />
                                ) : (<>
                                    {/* Desktop Table */}
                                    <div className="hidden lg:block overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                                            <tr>
                                                <th className={thClass}>Fournisseur</th>
                                                <th className={thClass}>Téléphone</th>
                                                <th className={thClass}>Ville</th>
                                                <th className={`${thClass} text-right`}>Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                            {filteredProviders.map((provider) => (
                                                <tr key={provider.id} onClick={() => handleOpenProviderModal(provider)}
                                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer">
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <Avatar name={provider.name}/>
                                                            <div className="min-w-0">
                                                                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{provider.name}</div>
                                                                {provider.email && (
                                                                    <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                                                                        <Mail className="w-3 h-3 flex-shrink-0"/>{provider.email}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{provider.phone || '—'}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{provider.city || '—'}</td>
                                                    <td className="px-6 py-4 text-right">
                                                        <RowActions actions={[
                                                            {label: 'Modifier', icon: Edit3, onClick: () => handleOpenProviderModal(provider)},
                                                            {label: 'Supprimer', icon: Trash2, tone: 'red', danger: true, onClick: () => handleDeleteProvider(provider.id)},
                                                        ]}/>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Cards */}
                                    <div className={mobileListClass}>
                                        {filteredProviders.map((provider) => (
                                            <div key={provider.id} className={`${mobileRowClass} cursor-pointer`} onClick={() => handleOpenProviderModal(provider)}>
                                                <div className="flex items-start gap-3 mb-3">
                                                    <Avatar name={provider.name} size="lg"/>
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{provider.name}</h3>
                                                        {provider.email && (
                                                            <p className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 truncate">
                                                                <Mail className="w-3 h-3 flex-shrink-0"/>{provider.email}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <RowActions actions={[
                                                        {label: 'Modifier', icon: Edit3, onClick: () => handleOpenProviderModal(provider)},
                                                        {label: 'Supprimer', icon: Trash2, tone: 'red', danger: true, onClick: () => handleDeleteProvider(provider.id)},
                                                    ]}/>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3 text-sm">
                                                    <div>
                                                        <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Téléphone</span>
                                                        <div className="font-semibold text-gray-900 dark:text-gray-100">{provider.phone || '—'}</div>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Ville</span>
                                                        <div className="font-semibold text-gray-900 dark:text-gray-100">{provider.city || '—'}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>)}
                            </ListCard>
                        )}

                        {!isLoading && providers.length > 0 && (
                            <Pagination page={1} totalPages={1} onPage={() => {}}
                                        summary={`${filteredProviders.length} fournisseur${filteredProviders.length !== 1 ? 's' : ''}`}/>
                        )}
                    </div>
                )}
            </div>

            {/* Modals */}
            {showFilterModal && <FilterModal/>}
            {showHistoryModal && <StockHistoryModal/>}
            {showAdjustModal && AdjustStockModal()}
            {showProviderModal && <ProviderModal isEdit={isProviderModalEdit} form={providerForm} setForm={setProviderForm} onClose={() => setShowProviderModal(false)} onSave={handleSaveProvider}/>}

            {/* Reference Picker Modals for Filters */}
            {activeFilterPicker === 'category' && (
                <ReferencePickerModal
                    config={categoriesConfig}
                    currentValue={selectedFilterCategory?.id}
                    onSelect={(item) => {
                        setSelectedFilterCategory(item);
                        setFilters(prev => ({...prev, category: item.id}));
                        setActiveFilterPicker(null);
                    }}
                    onClose={() => setActiveFilterPicker(null)}
                />
            )}
            {activeFilterPicker === 'brand' && (
                <ReferencePickerModal
                    config={brandsConfig}
                    currentValue={selectedFilterBrand?.id}
                    onSelect={(item) => {
                        setSelectedFilterBrand(item);
                        setFilters(prev => ({...prev, brand: item.id}));
                        setActiveFilterPicker(null);
                    }}
                    onClose={() => setActiveFilterPicker(null)}
                />
            )}

            {/* Export Modal */}
            {showExportModal && (
                <Modal title="Exporter le stock" subtitle="Sélectionnez les filtres d'exportation"
                       onClose={() => setShowExportModal(false)}
                       footer={<ModalFooter onCancel={() => setShowExportModal(false)} onConfirm={handleExportStock}
                                            saving={isExporting} label={isExporting ? 'Exportation...' : 'Exporter Excel'}/>}>
                    <div className="space-y-4">
                        {/* Category & Brand */}
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <FilterRefSelector
                                label="Catégorie"
                                value={selectedExportCategory}
                                placeholder="Toutes"
                                onClick={() => setActiveExportPicker('category')}
                                onClear={() => {
                                    setSelectedExportCategory(null);
                                    setExportFilters(prev => ({...prev, category: ''}));
                                }}
                            />
                            <FilterRefSelector
                                label="Marque"
                                value={selectedExportBrand}
                                placeholder="Toutes"
                                onClick={() => setActiveExportPicker('brand')}
                                onClear={() => {
                                    setSelectedExportBrand(null);
                                    setExportFilters(prev => ({...prev, brand: ''}));
                                }}
                            />
                        </div>

                        {/* Product name & SKU */}
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <div>
                                <label htmlFor="export-name" className={fieldLabelClass}>Nom du produit :</label>
                                <input id="export-name" type="text" value={exportFilters.name}
                                       onChange={(e) => setExportFilters(prev => ({...prev, name: e.target.value}))}
                                       placeholder="Rechercher..." className={providerFieldClass}/>
                            </div>
                            <div>
                                <label htmlFor="export-sku" className={fieldLabelClass}>SKU / Code-barres :</label>
                                <input id="export-sku" type="text" value={exportFilters.sku}
                                       onChange={(e) => setExportFilters(prev => ({...prev, sku: e.target.value}))}
                                       placeholder="Ex: 3760123..." className={providerFieldClass}/>
                            </div>
                        </div>

                        {/* Stock status */}
                        <div>
                            <label className={fieldLabelClass}>Statut du stock :</label>
                            <AppleSelect value={exportFilters.stock_status}
                                         onChange={(e) => setExportFilters(prev => ({...prev, stock_status: e.target.value}))}
                                         className={providerFieldClass}>
                                <option value="">Tous les statuts</option>
                                <option value="in_stock">En stock</option>
                                <option value="out_of_stock">Rupture de stock</option>
                            </AppleSelect>
                        </div>

                        {/* Stock range */}
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <div>
                                <label htmlFor="export-min" className={fieldLabelClass}>Stock minimum :</label>
                                <input id="export-min" type="number" value={exportFilters.stock_min}
                                       onChange={(e) => setExportFilters(prev => ({...prev, stock_min: e.target.value}))}
                                       placeholder="0" min="0" className={providerFieldClass}/>
                            </div>
                            <div>
                                <label htmlFor="export-max" className={fieldLabelClass}>Stock maximum :</label>
                                <input id="export-max" type="number" value={exportFilters.stock_max}
                                       onChange={(e) => setExportFilters(prev => ({...prev, stock_max: e.target.value}))}
                                       placeholder="∞" min="0" className={providerFieldClass}/>
                            </div>
                        </div>

                        {/* Critical only toggle */}
                        <div className="flex items-center justify-between gap-3 py-3 border-y border-gray-200 dark:border-gray-700">
                            <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0"/>
                                Produits sous seuil critique uniquement
                            </span>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={exportFilters.critical_only}
                                onClick={() => setExportFilters(prev => ({...prev, critical_only: !prev.critical_only}))}
                                className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors ${exportFilters.critical_only ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                            >
                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${exportFilters.critical_only ? 'translate-x-5' : ''}`}/>
                            </button>
                        </div>

                        <ModalMessage message={exportMessage}/>

                        <ResetLink onClick={() => {
                            setExportFilters({category:'',brand:'',name:'',sku:'',stock_status:'',critical_only:false,stock_min:'',stock_max:''});
                            setSelectedExportCategory(null);
                            setSelectedExportBrand(null);
                            setExportMessage(null);
                        }}/>
                    </div>
                </Modal>
            )}

            {/* Export Reference Picker Modals */}
            {activeExportPicker === 'category' && (
                <ReferencePickerModal
                    config={categoriesConfig}
                    currentValue={selectedExportCategory?.id}
                    onSelect={(item) => {
                        setSelectedExportCategory(item);
                        setExportFilters(prev => ({...prev, category: item.id}));
                        setActiveExportPicker(null);
                    }}
                    onClose={() => setActiveExportPicker(null)}
                />
            )}
            {activeExportPicker === 'brand' && (
                <ReferencePickerModal
                    config={brandsConfig}
                    currentValue={selectedExportBrand?.id}
                    onSelect={(item) => {
                        setSelectedExportBrand(item);
                        setExportFilters(prev => ({...prev, brand: item.id}));
                        setActiveExportPicker(null);
                    }}
                    onClose={() => setActiveExportPicker(null)}
                />
            )}

            {/* Stock Report Modal (PDF valuation report — GET /stock/report) */}
            {showReportModal && (
                <Modal title="Rapport de valorisation du stock"
                       subtitle="PDF comparant la valeur du stock au prix d'achat et au prix de vente"
                       onClose={() => setShowReportModal(false)}
                       footer={<ModalFooter onCancel={() => setShowReportModal(false)} onConfirm={handleGenerateStockReport}
                                            saving={isGeneratingReport} label={isGeneratingReport ? 'Génération...' : 'Télécharger le rapport'}/>}>
                    <div className="space-y-4">
                        {/* Category & Product */}
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <FilterRefSelector
                                label="Catégorie"
                                value={selectedReportCategory}
                                placeholder="Toutes"
                                onClick={() => setActiveReportPicker('category')}
                                onClear={() => {
                                    setSelectedReportCategory(null);
                                    setReportFilters(prev => ({...prev, category: ''}));
                                }}
                            />
                            <FilterRefSelector
                                label="Produit"
                                value={selectedReportProduct}
                                placeholder="Tous"
                                onClick={() => setActiveReportPicker('product')}
                                onClear={() => {
                                    setSelectedReportProduct(null);
                                    setReportFilters(prev => ({...prev, product: ''}));
                                }}
                            />
                        </div>

                        {/* Purchase date range (CUMP window — not the displayed stock date) */}
                        <div>
                            <label className={fieldLabelClass}>Période des achats pris en compte :</label>
                            <div className="grid grid-cols-2 gap-3">
                                <DateInput value={reportFilters.dateStart} aria-label="Date de début"
                                       onChange={(e) => setReportFilters(prev => ({...prev, dateStart: e.target.value}))}
                                       className={providerFieldClass}/>
                                <DateInput value={reportFilters.dateEnd} aria-label="Date de fin"
                                       onChange={(e) => setReportFilters(prev => ({...prev, dateEnd: e.target.value}))}
                                       className={providerFieldClass}/>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">Filtre uniquement les achats utilisés pour le coût moyen (CUMP) — le stock affiché reste le stock actuel</p>
                        </div>

                        <ModalMessage message={reportMessage}/>

                        <ResetLink onClick={() => {
                            setReportFilters({category: '', product: '', dateStart: '', dateEnd: ''});
                            setSelectedReportCategory(null);
                            setSelectedReportProduct(null);
                            setReportMessage(null);
                        }}/>
                    </div>
                </Modal>
            )}

            {/* Report Reference Picker Modals */}
            {activeReportPicker === 'category' && (
                <ReferencePickerModal
                    config={categoriesConfig}
                    currentValue={selectedReportCategory?.id}
                    onSelect={(item) => {
                        setSelectedReportCategory(item);
                        setReportFilters(prev => ({...prev, category: item.id}));
                        setActiveReportPicker(null);
                    }}
                    onClose={() => setActiveReportPicker(null)}
                />
            )}
            {activeReportPicker === 'product' && (
                <ReferencePickerModal
                    config={productsConfig}
                    currentValue={selectedReportProduct?.id}
                    onSelect={(item) => {
                        setSelectedReportProduct(item);
                        setReportFilters(prev => ({...prev, product: item.id}));
                        setActiveReportPicker(null);
                    }}
                    onClose={() => setActiveReportPicker(null)}
                />
            )}

            {/* Import Modal */}
            {showImportModal && (
                <Modal title="Importer le stock" subtitle="Importez vos produits depuis un fichier Excel"
                       onClose={() => setShowImportModal(false)}
                       footer={importResult?.type === 'success' ? (
                           <button type="button" onClick={() => setShowImportModal(false)}
                                   className="w-full py-4 border-t border-gray-200 dark:border-gray-700 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                               Fermer
                           </button>
                       ) : (
                           <ModalFooter onCancel={() => setShowImportModal(false)} onConfirm={handleImportStock}
                                        saving={isImporting} disabled={!importFile}
                                        label={isImporting ? 'Importation...' : 'Importer'}/>
                       )}>
                    <div className="space-y-4">
                        {/* File format info */}
                        <div>
                            <p className={`${fieldLabelClass} flex items-center gap-2`}>
                                <FileSpreadsheet className="w-4 h-4 text-primary"/>
                                Format du fichier Excel (.xlsx) :
                            </p>
                            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Colonne</th>
                                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Champ</th>
                                        <th className="px-4 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Requis</th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 text-gray-700 dark:text-gray-300">
                                    {[
                                        ['A', 'Nom produit', true], ['B', 'Marque', false], ['C', 'Catégorie', true],
                                        ['D', 'SKU', false], ['E', 'Quantité', true], ['F', 'Prix', false], ['G', 'Code-barres', false],
                                    ].map(([col, field, required]) => (
                                        <tr key={col}>
                                            <td className="px-4 py-1.5 font-mono text-gray-500 dark:text-gray-400">{col}</td>
                                            <td className="px-4 py-1.5">{field}</td>
                                            <td className={`px-4 py-1.5 text-center ${required ? 'text-primary font-semibold' : 'text-gray-400'}`}>{required ? '✓' : '—'}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* File upload zone */}
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                                importFile
                                    ? 'border-primary bg-primary/5'
                                    : 'border-gray-300 dark:border-gray-600 hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx"
                                onChange={handleFileSelect}
                                className="hidden"
                            />
                            {importFile ? (
                                <div className="flex flex-col items-center gap-2">
                                    <FileSpreadsheet className="w-8 h-8 text-primary"/>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{importFile.name}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{(importFile.size / 1024).toFixed(1)} KB</p>
                                    <Button variant="danger" size="sm" icon={Trash2} onClick={(e) => {
                                        e.stopPropagation();
                                        setImportFile(null);
                                        setImportResult(null);
                                    }}>
                                        Supprimer
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <Upload className="w-8 h-8 text-gray-400 dark:text-gray-500"/>
                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Cliquez pour sélectionner un fichier</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Format .xlsx uniquement</p>
                                </div>
                            )}
                        </div>

                        {/* Import result */}
                        {importResult && (
                            <div className="space-y-3">
                                <ModalMessage message={{type: importResult.type, text: importResult.message}}/>

                                {/* Statistics — same hairline strip as the Stock totals */}
                                {importResult.statistics && (
                                    <div className="grid grid-cols-2 gap-px bg-gray-200 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                        {[
                                            importResult.statistics.products && {label: 'Produits créés', value: importResult.statistics.products.created || 0, color: 'text-green-600 dark:text-green-400'},
                                            importResult.statistics.products && {label: 'Produits mis à jour', value: importResult.statistics.products.updated || 0, color: 'text-blue-600 dark:text-blue-400'},
                                            importResult.statistics.categories && {label: 'Catégories créées', value: importResult.statistics.categories.created || 0},
                                            importResult.statistics.brands && {label: 'Marques créées', value: importResult.statistics.brands.created || 0},
                                            importResult.statistics.stock_movements && {label: 'Mouvements créés', value: importResult.statistics.stock_movements.created || 0},
                                            importResult.statistics.rows_skipped > 0 && {label: 'Lignes ignorées', value: importResult.statistics.rows_skipped, color: 'text-amber-600 dark:text-amber-400'},
                                        ].filter(Boolean).map(stat => (
                                            <div key={stat.label} className="bg-white dark:bg-gray-800 px-4 py-2.5">
                                                <span className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{stat.label}</span>
                                                <span className={`block text-lg font-bold tabular-nums ${stat.color || 'text-gray-900 dark:text-gray-100'}`}>{stat.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Reference */}
                                {importResult.importReference && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Réf : <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-700 dark:text-gray-300">{importResult.importReference}</span>
                                    </p>
                                )}

                                {/* Errors list */}
                                {importResult.errors && importResult.errors.length > 0 && (
                                    <div>
                                        <p className="text-xs font-semibold text-red-600 dark:text-red-400 mb-1">Erreurs :</p>
                                        <ul className="text-xs max-h-24 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg text-red-600 dark:text-red-400">
                                            {importResult.errors.map((err, i) => (
                                                <li key={i} className="px-3 py-1.5">{err}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Stock;
